import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Clock, Send } from '../../common/Icons';
import toast from 'react-hot-toast';

const CounterProposalModal = ({ request, onSubmit, onClose }) => {
  const [counterDate, setCounterDate] = useState('');
  const [counterTime, setCounterTime] = useState('');
  const [responseNotes, setResponseNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!counterDate || !counterTime) {
      toast.error('Please select both date and time for your counter-proposal');
      return;
    }

    // Validate date is not in the past
    const proposedDateTime = new Date(`${counterDate}T${counterTime}`);
    if (proposedDateTime < new Date()) {
      toast.error('Counter-proposal date/time cannot be in the past');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(request.request_id, 'counter_propose', {
        responseNotes,
        counterDate,
        counterTime
      });
      onClose();
    } catch (error) {
      console.error('Error submitting counter-proposal:', error);
      toast.error('Failed to submit counter-proposal');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#1e2129] backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 text-white p-6 max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Counter-Propose Schedule</h2>
            <p className="text-sm text-zinc-400 mt-1">Suggest an alternative time</p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Original Proposal Info */}
        <div className="bg-[#22262f] border border-white/10 rounded-xl p-4 mb-6">
          <p className="text-sm text-zinc-400 mb-2">Original Proposal:</p>
          <div className="flex items-center space-x-2 text-white">
            <Calendar className="w-5 h-5 text-[#23F8B5]" />
            <span>{new Date(request.proposed_date).toLocaleDateString()}</span>
            <Clock className="w-5 h-5 text-[#23F8B5] ml-2" />
            <span>{request.proposed_time}</span>
          </div>
          {request.proposer_notes && (
            <p className="text-sm text-zinc-300 mt-2">
              <span className="text-zinc-400">Notes:</span> {request.proposer_notes}
            </p>
          )}
        </div>

        {/* Counter-Proposal Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Proposed Date
            </label>
            <input
              type="date"
              value={counterDate}
              onChange={(e) => setCounterDate(e.target.value)}
              min={today}
              required
              className="w-full bg-[#22262f] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#31C590] transition-colors [color-scheme:dark]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Proposed Time
            </label>
            <input
              type="time"
              value={counterTime}
              onChange={(e) => setCounterTime(e.target.value)}
              required
              className="w-full bg-[#22262f] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#31C590] transition-colors [color-scheme:dark]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={responseNotes}
              onChange={(e) => setResponseNotes(e.target.value)}
              placeholder="Explain why this time works better..."
              rows={3}
              className="w-full bg-[#22262f] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#31C590] transition-colors [color-scheme:dark] resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-[#22262f] border border-white/10 hover:bg-[#262b35] disabled:opacity-50 text-gray-300 py-2.5 px-4 rounded-xl font-semibold text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-900 py-2.5 px-4 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
            >
              <Send className="w-4 h-4 flex-shrink-0" />
              <span>{isSubmitting ? 'Sending...' : 'Send Counter'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CounterProposalModal;